import {
    animate,
    AnimationEvent,
    style,
    transition,
    trigger,
} from "@angular/animations";
import { BreakpointState } from "@angular/cdk/layout";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { combineLatest, Observable, Subject, Subscription } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { Product } from "@hermes/api-model-product";
import { Context, WINDOW } from "@hermes/app-core";
import { AnalyticsService } from "@hermes/utils/analytics";
import { Logger } from "@hermes/logger";

import {
    BEAUTY_VIRTUAL_TRY_ON,
    VIRTUAL_TRY_ON,
    FeatureFlagFacade,
} from "@hermes/states/flipper";
import {
    DEFAULT_VIEW_SIZE,
    SECOND_EDITO_LIMIT_MOBILE_LIST,
    SECOND_EDITO_LIMIT_MOBILE,
    SECOND_EDITO_LIMIT_DESKTOP,
    HERO_POSITIONS_IN_GRID,
    DisplayMode,
    GridEdito,
    GridFacade,
    GridPageHeadService,
    GridProduct,
    GridProductLazyLoadedEvent,
    GTMHelper,
} from "@hermes/states/grid-filters-state";
import { skuToId } from "@hermes/utils-generic/helpers";
import { FocusService } from "@hermes/utils-generic/services/focus";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";
import { LOGGER } from "@hermes/web-logger";

import { ProductInViewportService } from "../../services/product-in-viewport-service/product-in-viewport.service";
import { GridResultFooterComponent } from "../grid-result-footer/grid-result-footer.component";

/**
 * Result container implementing headers, products (result items) and editos
 */
@Component({
    selector: "h-grid-results",
    templateUrl: "./grid-results.component.html",
    styleUrls: ["./grid-results.component.scss"],
    animations: [
        trigger("fadeIn", [
            transition("hidden => displayed", [
                style({
                    opacity: 0,
                    transform: "translateY(60px)",
                }),
                animate(
                    "300ms ease-in",
                    style({
                        opacity: 1,
                        transform: "translateY(0px)",
                    }),
                ),
            ]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridResultsComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    public isSearchGrid!: boolean;

    @ViewChild("gridResultsUl")
    public gridResultsUl!: ElementRef;

    @ViewChild(GridResultFooterComponent, { static: true })
    public gridFooterComponent!: GridResultFooterComponent;

    public products: GridProduct[] = [];

    public editos: GridEdito[] = [];

    public resultItems: Array<GridEdito | GridProduct> = [];

    public displayMode?: DisplayMode;

    public shouldFocusEl = false;
    public haveToFocusEl = true;

    public fadeInAnimationState = "hidden";

    public isMobile = false;

    public isGridHeroProduct: boolean = false;

    public gridRendered$: Subject<void> = new Subject<void>();

    public isVtoEnabled$: Observable<boolean> = this.isVtoEnabled();

    public readonly PRODUCT = "product";
    public readonly EDITO = "edito";
    public gridProductInterface!: GridProduct;
    public gridEditoInterface!: GridEdito;

    /** Observables streams */
    private subscription: Subscription = new Subscription();
    private currentPositionInPage?: number;

    constructor(
        @Inject(LOGGER) private logger: Logger,
        @Inject(WINDOW) private window: Window,
        private gridFacade: GridFacade,
        private analyticsService: AnalyticsService,
        private breakpointService: BreakpointService,
        private context: Context,
        private detector: ChangeDetectorRef,
        private focusService: FocusService,
        private gridPageHeadService: GridPageHeadService,
        private gtmHelper: GTMHelper,
        private featureFlagFacade: FeatureFlagFacade,
        private productInViewportService: ProductInViewportService,
    ) {}

    public get seoText$(): Observable<string | undefined> {
        return this.gridFacade.seoText$;
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.gridFacade.resultItems$.subscribe((result) => {
                this.gridRendered$.next();
                this.initResultItems(result);
            }),
        );

        this.subscription.add(
            this.breakpointService
                .mediumBreakpointObserver()
                .pipe(distinctUntilChanged())
                .subscribe((value: BreakpointState) => {
                    this.isMobile = !value.matches;
                    this.detector.markForCheck();
                }),
        );
        this.subscription.add(
            this.gridFacade.displayMode$
                .pipe(
                    // only in browsermode
                    filter(() => this.context.isInBrowserMode()),
                    // always apply display mode on product list
                    tap((mode: DisplayMode) => {
                        if (this.displayMode !== undefined) {
                            this.fadeInAnimationState = "displayed";
                        }

                        this.displayMode = mode;

                        this.getFullCollection();
                        this.detector.markForCheck();
                    }),
                    // do not scroll to top on grid init
                    filter((_value, index) => index > 0),
                )
                .subscribe(() => {
                    this.window.scrollTo(0, 0);
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                combineLatest([
                    this.gridFacade.isGridHeroProduct$,
                    this.gridFacade.resultItems$,
                ]).subscribe(([isGridHeroProduct, resultItems]) => {
                    this.isGridHeroProduct = isGridHeroProduct;
                    if (isGridHeroProduct && Boolean(resultItems)) {
                        this.updateItemsOnDisplayChange(HERO_POSITIONS_IN_GRID);
                    }
                }),
            );
        }

        this.addSubscriptionProductsLoadedInViewPort();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.productInViewportService.clearProductsLoadInViewPort();
    }

    public ngAfterViewInit(): void {
        if (this.shouldFocusEl && this.products && this.editos) {
            this.shouldFocusEl = false;
            this.focusGridResultItemFromLoadMore(
                this.products.length,
                this.editos.length,
                this.gridResultsUl.nativeElement,
            );
            this.detector.markForCheck();
        }
    }

    /**
     * Load more focus
     */
    public focusGridResultItemFromLoadMore(
        productSize: number,
        editoSize: number,
        gridElement: HTMLUListElement,
    ): void {
        // calc the grid step
        const step = Math.floor(productSize / DEFAULT_VIEW_SIZE);

        // get the remainder of division
        const remainder = productSize % DEFAULT_VIEW_SIZE;

        // calc the first new 36 ( or less ) item in current dom ul element
        let index = remainder
            ? productSize - remainder
            : (step - 1) * DEFAULT_VIEW_SIZE;

        // get the number of edito
        index = index + editoSize;

        this.focusService.focusElementInList(gridElement, index);
    }

    public trackById(index: number, item: GridEdito | GridProduct): string {
        if ("nodeId" in item) {
            return `${index}-${item.nodeId}`;
        }
        return `${index}-${item.sku}`;
    }

    /**
     * Retrieves a collection of products with editos based on the specified limits.
     * @param secondEditoLimit - The limit to include the second edito.
     * @param numberHeroProduct - The number of hero-type products to include.
     * @returns A collection of editos and products based on the specified limits and parameters.
     */
    public getCollectionWithEdito(
        secondEditoLimit: number,
        numberHeroProduct: number,
    ): Array<GridEdito | GridProduct> {
        let fullCollection: Array<GridEdito | GridProduct> = [];

        const editos: GridEdito[] = this.editos || [];
        fullCollection = [...this.products];

        const numberHeroProductOnGrid = this.isGridHeroProduct
            ? numberHeroProduct
            : 0;

        if (
            this.products.length >=
            secondEditoLimit - numberHeroProductOnGrid
        ) {
            fullCollection = [...this.products, ...editos];
            return fullCollection;
        }

        if (editos[0] !== undefined) {
            fullCollection.push(editos[0]);
        }

        return fullCollection;
    }

    /**
     * Retrieves a collection of products with editos injected based on the specified conditions.
     * @returns A collection of editos and products based on specific conditions and parameters.
     */
    public getFullCollection(): Array<GridEdito | GridProduct> {
        let fullCollection: Array<GridEdito | GridProduct> = [];

        const editos: GridEdito[] = this.editos || [];

        fullCollection = [...this.products];

        if (editos.length === 0) {
            return fullCollection;
        }

        if (!this.isMobile) {
            return this.getCollectionWithEdito(SECOND_EDITO_LIMIT_DESKTOP, 4);
        } else if (this.displayMode === "list") {
            return this.getCollectionWithEdito(
                SECOND_EDITO_LIMIT_MOBILE_LIST,
                0,
            );
        }
        return this.getCollectionWithEdito(SECOND_EDITO_LIMIT_MOBILE, 2);
    }

    /**
     * @returns ID for item
     * @param item Item to handle
     */
    public getItemId(item: GridEdito | GridProduct): string {
        if (!this.isGridProduct(item)) {
            let index = 1;
            if (this.editos[0].nodeId === item.nodeId) {
                index = 0;
            }
            return `grid-result-edito-${index}`;
        }
        try {
            return `grid-product-${skuToId(item.sku)}`;
        } catch {
            this.logger.warn("no-sku", item);
            return "grid-product-nosku";
        }
    }

    /**
     * @returns if the item is a Product
     * @param item Item to test
     */
    public isGridProduct(item: GridProduct | GridEdito): item is GridProduct {
        return !Object.prototype.hasOwnProperty.call(item, "nodeId");
    }

    /**
     * Allow to define if the edito element position is even
     */
    public isEvenEdito(edito: GridEdito): boolean {
        return this.editos.indexOf(edito) % 2 === 0;
    }

    /**
     * When fade in animation is done
     *
     * @param event: AnimationEvent
     */
    public whenFadeInDone(event: AnimationEvent): void {
        if (event.fromState === "hidden" && event.toState === "displayed") {
            this.fadeInAnimationState = "hidden";
            this.detector.markForCheck();
        }
    }

    private initResultItems(result: {
        products: GridProduct[];
        editos: GridEdito[];
    }): void {
        if (!result) {
            // cleanup existing results
            this.shouldFocusEl = false;
            this.products = [];
            this.editos = [];
            this.resultItems = [];
            return;
        }
        // Add preload image link for the first displayed product.
        this.gridPageHeadService.addPreloadImageLink(
            result.products[0] as unknown as Product,
            this.isMobile,
        );
        // activate focus only on activated load more and big products collection size
        if (
            result.products.length > DEFAULT_VIEW_SIZE &&
            this.gridFooterComponent.activated &&
            this.haveToFocusEl
        ) {
            this.shouldFocusEl = true;
            // only focus one time after click
            this.haveToFocusEl = false;
        }
        this.products = result.products;
        this.editos = result.editos;
        this.resultItems = this.getFullCollection();
        this.detector.markForCheck();
    }

    /**
     * Update item.isHero to grow image resolution
     */
    private updateItemsOnDisplayChange(heroPositions: number[]): void {
        if (this.products && heroPositions.length > 0) {
            // We set the isHero parameter value for each product
            this.products.forEach((item: GridProduct, index) => {
                item.isHero = heroPositions.includes(
                    (index + 1) % DEFAULT_VIEW_SIZE,
                );
            });
            // Reconstruction of this.resultItems list and re-render.
            this.resultItems = this.getFullCollection();
            this.detector.detectChanges();
        }
    }

    private addSubscriptionProductsLoadedInViewPort(): void {
        this.subscription.add(
            this.productInViewportService.productsLoadedInViewPort$
                .pipe(
                    debounceTime(100),
                    withLatestFrom(
                        this.gridFacade.isFromPreviousGrid$,
                        this.gridFacade.positionInPage$,
                    ),
                    tap(
                        ([
                            _productsLoadedInViewPort,
                            isFromPreviousGrid,
                            positionInPage,
                        ]) => {
                            if (isFromPreviousGrid) {
                                this.currentPositionInPage = positionInPage;
                                this.gridFacade.scrollToPreviousProduct();
                            }
                        },
                    ),
                    filter(
                        ([productsLoadedInViewPort, isFromPreviousGrid]) =>
                            productsLoadedInViewPort.length > 0 &&
                            (!isFromPreviousGrid ||
                                (this.currentPositionInPage !== undefined &&
                                    this.currentPositionInPage <
                                        this.window.innerHeight)),
                    ),
                    map(
                        ([productsLoadedInViewPort]) =>
                            productsLoadedInViewPort,
                    ),
                    withLatestFrom(this.gridFacade.pageViewType$),
                    tap(([productsLoadedInViewPort, pageViewType]) => {
                        const pageEventDatas = this.gtmHelper.formatPageEvent(
                            productsLoadedInViewPort,
                            pageViewType,
                        );
                        pageEventDatas?.forEach((pageEventData) => {
                            this.analyticsService.sendData(
                                new GridProductLazyLoadedEvent(pageEventData),
                            );
                        });
                        this.productInViewportService.productsLoadInViewPortAnalyticPrint();
                        this.currentPositionInPage = undefined;
                    }),
                )
                .subscribe(),
        );
    }

    private isVtoEnabled(): Observable<boolean> {
        return combineLatest([
            this.featureFlagFacade.isActivated(BEAUTY_VIRTUAL_TRY_ON),
            this.featureFlagFacade.isActivated(VIRTUAL_TRY_ON),
        ]).pipe(
            map(
                ([isBeautyVtoEnabled, isJewelryVtoEnabled]) =>
                    isBeautyVtoEnabled || isJewelryVtoEnabled,
            ),
        );
    }
}
